import { BASE_URL, ORGANIZATION_ID } from '@/lib/metadata-helper';
import { PRICING_FAQS } from '@/lib/pricing-faqs';

/**
 * Structured data for `/pricing`.
 *
 * The numbers here mirror what the rendered page actually shows, per locale,
 * and nothing else. Schema that claims a price the page does not display is a
 * policy violation, so the two locales are described separately rather than
 * converted into one another:
 *
 *   - `/pricing`    shows a USD range, "$0.75 – $0.90 per resolved ticket",
 *     with the rate set by monthly volume.
 *   - `/ar/pricing` shows the same range in riyal, "٢ - ٣ ⃁",
 *     converted at the 3.75 SAR/USD peg and rounded to whole riyal, because
 *     the Arabic page states the price without decimals.
 *
 * The Arabic page used to show a single figure where the English showed a
 * range, which is why this file once documented a discrepancy. It is resolved:
 * both locales publish a range, each in its own currency. The conversion lives
 * in the copy, not here — schema still only mirrors what each page displays.
 *
 * Everything else the page states and this encodes: human-handled tickets are
 * free, and there are no setup, per-seat, per-message or per-channel fees.
 */

interface LocalePricing {
  currency: string;
  /** Low end of the published rate, or the flat rate when there is no range. */
  minPrice: number;
  /** Top of the published range. Equal to minPrice when the page shows one figure. */
  maxPrice: number;
  unitText: string;
}

const PRICING_BY_LOCALE: Record<string, LocalePricing> = {
  en: {
    currency: 'USD',
    minPrice: 0.75,
    maxPrice: 0.9,
    unitText: 'resolved ticket',
  },
  ar: {
    currency: 'SAR',
    minPrice: 2,
    maxPrice: 3,
    unitText: 'تذكرة محلولة',
  },
};

export function buildPricingSchema(locale: string): Record<string, unknown> {
  const isAr = locale === 'ar';
  const url = isAr ? `${BASE_URL}/ar/pricing` : `${BASE_URL}/pricing`;

  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': `${url}#software`,
    name: 'Open',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: BASE_URL,
    inLanguage: isAr ? 'ar' : 'en',
    provider: { '@id': ORGANIZATION_ID },
    description: isAr
      ? 'منصة Open لخدمة العملاء بالذكاء الاصطناعي. تدفع فقط عن التذاكر التي يحلّها Open بالكامل.'
      : "Open's AI customer service platform. You pay only for tickets Open fully resolves end-to-end, across voice, chat, email, WhatsApp and social.",
    offers: buildOffer(locale),
  };
}

/**
 * The published rate as an `Offer`, in the locale's own currency.
 *
 * Exported because every `SoftwareApplication` node on the site needs one —
 * Google treats a software item with no price, rating or review as incomplete
 * — and the point of keeping it here is that all of them read the same
 * `PRICING_BY_LOCALE` entry the /pricing page renders. There is one number,
 * in one file, however many pages quote it.
 */
export function buildOffer(locale: string): Record<string, unknown> {
  const isAr = locale === 'ar';
  const p = PRICING_BY_LOCALE[isAr ? 'ar' : 'en'];
  const url = isAr ? `${BASE_URL}/ar/pricing` : `${BASE_URL}/pricing`;

  return {
    '@type': 'Offer',
    url,
    priceCurrency: p.currency,
    // A usage-based rate, so the unit matters as much as the number.
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      priceCurrency: p.currency,
      minPrice: p.minPrice,
      maxPrice: p.maxPrice,
      unitText: p.unitText,
      referenceQuantity: {
        '@type': 'QuantitativeValue',
        value: 1,
        unitText: p.unitText,
      },
    },
    availability: 'https://schema.org/InStock',
  };
}

/**
 * FAQPage for `/pricing`, built from the same array the page renders.
 *
 * The questions are not restated here — they are imported — so this can never
 * describe an FAQ the page does not show.
 */
export function buildPricingFaqSchema(locale: string): Record<string, unknown> {
  const isAr = locale === 'ar';
  const url = isAr ? `${BASE_URL}/ar/pricing` : `${BASE_URL}/pricing`;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${url}#faq`,
    inLanguage: isAr ? 'ar' : 'en',
    mainEntity: PRICING_FAQS[isAr ? 'ar' : 'en'].map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}
